import React from "react";
import { Alert, Button, Snackbar, Alert as MuiAlert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ListPageShell } from "../../components/organisms/list";
import useUserManagement from "./useUserManagement";
import { useHasPermission } from "../../hooks/useHasPermission";
import UsersToolbar from "./UsersToolbar";
import UsersList from "./UsersList";
import UserActionsMenu from "./UserActionsMenu";
import UserDetailDrawer from "./UserDetailDrawer";
import UserActionDialog from "./UserActionDialog";
import UserDeleteDialog from "./UserDeleteDialog";
import CreateAdminStaffDrawer from "./CreateAdminStaffDrawer";

const SCOPE_CONFIG = {
  patients: { roles: ["Patient", "PatientRelative"], title: "Hastalar", subtitle: "Hasta ve hasta yakını kullanıcıları yönetin", canAdd: false },
  clinic: { roles: ["ClinicStaff"], title: "Klinik Kullanıcıları", subtitle: "Kliniklerdeki kullanıcıları görüntüleyin", canAdd: false },
  staff: { roles: ["AdminStaff"], title: "Personeller", subtitle: "Sistem personelini yönetin", canAdd: true },
};

export default function UserManagement({ scope }) {
  const scopeCfg = scope ? SCOPE_CONFIG[scope] : null;
  const vm = useUserManagement({ scopeRoles: scopeCfg?.roles, scopeKey: scope || "all" });
  const navigate = useNavigate();
  const canCreate = useHasPermission("users:create");
  const canUpdate = useHasPermission("users:update");
  const canDelete = useHasPermission("users:delete");
  const canUseActions = canUpdate || canDelete;

  return (
    <ListPageShell>
      <UsersToolbar
        filters={vm.filters}
        onFilterChange={vm.setFilter}
        onClear={vm.clearFilters}
        pending={vm.pending}
        counts={vm.counts}
        filteredCount={vm.filteredUsers.length}
        isLoading={vm.isLoadingUsers}
        onCreate={vm.openAdminStaffDrawer}
        canCreate={canCreate && (scopeCfg ? scopeCfg.canAdd : true)}
        title={scopeCfg?.title || "Kullanıcı Yönetimi"}
        subtitle={scopeCfg?.subtitle || "Sistem kullanıcılarını yönetin ve izleyin"}
        scope={scope}
      />

      {vm.usersError ? (
        <Alert
          severity="error"
          sx={{ borderRadius: 0 }}
          action={
            <Button
              variant="outlined"
              onClick={vm.refreshUsers}
              sx={{ fontWeight: 800, textTransform: "none" }}
            >
              Tekrar Dene
            </Button>
          }
        >
          Kullanıcı listesi yüklenirken bir hata oluştu
        </Alert>
      ) : null}

      <UsersList
        users={vm.pagedUsers}
        isLoading={vm.isLoadingUsers}
        page={vm.page}
        pageSize={vm.pageSize}
        totalItems={vm.filteredUsers.length}
        totalPages={vm.totalPages}
        onPageChange={vm.setPage}
        onPageSizeChange={vm.setPageSize}
        onOpenDetail={vm.openDetailFor}
        onMenuOpen={vm.openMenu}
        onClinicClick={(clinicId) => navigate(`/Klinikler/${clinicId}`)}
        canUseActions={canUseActions}
      />

      <UserActionsMenu
        anchorEl={vm.menuAnchorEl}
        user={vm.menuUser}
        onClose={vm.closeMenu}
        onDetail={vm.openDetail}
        onAction={vm.openActionDialog}
        onDelete={vm.openDeleteDialog}
        canUpdate={canUpdate}
        canDelete={canDelete}
      />

      <UserDetailDrawer
        open={vm.detailDrawerOpen}
        onClose={() => vm.setDetailDrawerOpen(false)}
        user={vm.selectedUser}
      />

      <UserActionDialog
        open={vm.dialogOpen}
        dialogType={vm.dialogType}
        selectedUser={vm.selectedUser}
        reason={vm.reason}
        onReasonChange={vm.setReason}
        endDate={vm.endDate}
        onEndDateChange={vm.setEndDate}
        onClose={vm.closeActionDialog}
        onConfirm={vm.handleAccountAction}
        isLoading={vm.isActionLoading}
      />

      <UserDeleteDialog
        open={vm.deleteDialogOpen}
        user={vm.deleteTargetUser}
        onClose={() => { vm.setDeleteDialogOpen(false); vm.setDeleteTargetUser(null); }}
        onConfirm={vm.handleDeleteConfirm}
        isDeleting={vm.isDeleting}
      />

      <CreateAdminStaffDrawer
        open={vm.adminStaffDrawerOpen}
        onClose={vm.closeAdminStaffDrawer}
        form={vm.adminStaffForm}
        setField={vm.setAdminStaffField}
        onSubmit={vm.handleCreateAdminStaff}
        isLoading={vm.isCreatingAdminStaff}
        formError={vm.adminStaffFormError}
      />

      <Snackbar
        open={vm.snackbar.open}
        autoHideDuration={6000}
        onClose={vm.closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={vm.closeSnackbar} severity={vm.snackbar.severity} sx={{ width: "100%" }}>
          {vm.snackbar.message}
        </MuiAlert>
      </Snackbar>
    </ListPageShell>
  );
}
